import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { Button, CircularProgress, Grid, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CreditCardIcon from '@material-ui/icons/CreditCardTwoTone';
import RefreshIcon from '@material-ui/icons/Refresh';
import { withStyles } from '@material-ui/core/styles';
import { actions as paymentCardActions } from '../../actions/paymentCardActions';
import ConfirmationButton from '../common/ConfirmationButton';
import i18n from '../../i18n';

const styles = () => ({
  root: {
    marginTop: 14,
    marginBottom: 14,
    paddingTop: 12,
    borderTop: '1px solid rgba(255, 255, 255, 0.35)',
    color: '#fff',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 4,
  },
  icon: {
    fontSize: 18,
    marginRight: 8,
  },
  body: {
    opacity: 0.9,
  },
  cardPreview: {
    marginTop: 8,
    padding: '10px 12px',
    borderRadius: 4,
    border: '1px solid rgba(255, 255, 255, 0.28)',
    background: 'rgba(255, 255, 255, 0.14)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.16)',
  },
  cardTopLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
  },
  issuer: {
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  activeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 7px',
    borderRadius: 12,
    background: 'rgba(0, 0, 0, 0.18)',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.4,
  },
  activeIcon: {
    fontSize: 13,
    marginRight: 4,
  },
  cardMask: {
    marginTop: 4,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.35,
    letterSpacing: 0,
  },
  cardMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px 18px',
    marginTop: 6,
  },
  metaItem: {
    fontSize: 11,
    lineHeight: 1.5,
  },
  metaLabel: {
    opacity: 0.72,
    marginRight: 4,
  },
  detachButton: {
    color: '#fff',
    borderColor: 'rgba(255, 255, 255, 0.55)',
    '&:hover': {
      borderColor: '#fff',
      background: 'rgba(255, 255, 255, 0.10)',
    },
  },
  actions: {
    marginTop: 8,
  },
  actionButton: {
    marginRight: 8,
    marginTop: 4,
  },
});

class PaymentCardPanel extends React.Component {
  handleStartActivation = () => this.props.actions.startActivation();

  handleResumeActivation = () => this.props.actions.resumeActivation();

  handleRefresh = () => this.props.actions.load();

  handleDeactivate = () => this.props.actions.deactivate();

  formatDate(date) {
    return date ? moment(date).format('DD.MM.YYYY') : i18n.t('wallet.paymentCard.unknown');
  }

  renderLoading() {
    return (
      <Grid container alignItems="center" spacing={1}>
        <Grid item>
          <CircularProgress size={16} color="inherit" />
        </Grid>
        <Grid item>
          <Typography variant="caption" color="inherit">
            {i18n.t('wallet.paymentCard.loading')}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  renderNoCard() {
    const { classes, paymentCard } = this.props;

    return (
      <React.Fragment>
        <Typography variant="caption" color="inherit" className={classes.body} display="block">
          {i18n.t('wallet.paymentCard.noCardDescription')}
        </Typography>
        <div className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            size="small"
            className={classes.actionButton}
            disabled={paymentCard.actionInProgress}
            onClick={this.handleStartActivation}
          >
            {i18n.t('wallet.paymentCard.attachButton')}
          </Button>
        </div>
      </React.Fragment>
    );
  }

  renderPendingActivation() {
    const { classes, paymentCard } = this.props;

    return (
      <React.Fragment>
        <Typography variant="caption" color="inherit" display="block">
          {i18n.t('wallet.paymentCard.pendingActivation')}
        </Typography>
        <div className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            size="small"
            className={classes.actionButton}
            disabled={paymentCard.actionInProgress}
            onClick={this.handleResumeActivation}
          >
            {i18n.t('wallet.paymentCard.continueActivationButton')}
          </Button>
          <Button
            color="default"
            variant="contained"
            size="small"
            className={classes.actionButton}
            disabled={paymentCard.fetching || paymentCard.actionInProgress}
            onClick={this.handleRefresh}
          >
            <RefreshIcon style={{ fontSize: 16, marginRight: 4 }} />
            {i18n.t('wallet.paymentCard.refreshButton')}
          </Button>
        </div>
      </React.Fragment>
    );
  }

  renderPendingDeactivation() {
    const { classes, paymentCard } = this.props;

    return (
      <React.Fragment>
        <Typography variant="caption" color="inherit" display="block">
          {i18n.t('wallet.paymentCard.pendingDeactivation')}
        </Typography>
        <div className={classes.actions}>
          <Button
            color="default"
            variant="contained"
            size="small"
            className={classes.actionButton}
            disabled
          >
            {i18n.t('wallet.paymentCard.detachButton')}
          </Button>
          <Button
            color="default"
            variant="contained"
            size="small"
            className={classes.actionButton}
            disabled={paymentCard.fetching || paymentCard.actionInProgress}
            onClick={this.handleRefresh}
          >
            <RefreshIcon style={{ fontSize: 16, marginRight: 4 }} />
            {i18n.t('wallet.paymentCard.refreshButton')}
          </Button>
        </div>
      </React.Fragment>
    );
  }

  renderActiveCard() {
    const { classes, paymentCard } = this.props;
    const { data } = paymentCard;
    const issuer = data.issuer || i18n.t('wallet.paymentCard.unknown');
    const mask = data.mask || i18n.t('wallet.paymentCard.unknown');

    return (
      <React.Fragment>
        <div className={classes.cardPreview}>
          <div className={classes.cardTopLine}>
            <span className={classes.issuer}>{issuer}</span>
            <span className={classes.activeBadge}>
              <CheckCircleIcon className={classes.activeIcon} />
              {i18n.t('wallet.paymentCard.activeStatus')}
            </span>
          </div>
          <div className={classes.cardMask}>{mask}</div>
          <div className={classes.cardMeta}>
            <span className={classes.metaItem}>
              <span className={classes.metaLabel}>{i18n.t('wallet.paymentCard.expirationDateLabel')}</span>
              <strong>{this.formatDate(data.expirationDate)}</strong>
            </span>
            <span className={classes.metaItem}>
              <span className={classes.metaLabel}>{i18n.t('wallet.paymentCard.activatedAtLabel')}</span>
              <strong>{this.formatDate(data.activatedAt)}</strong>
            </span>
          </div>
        </div>
        <div className={classes.actions}>
          <ConfirmationButton
            color="default"
            variant="outlined"
            size="small"
            className={`${classes.actionButton} ${classes.detachButton}`}
            disabled={paymentCard.actionInProgress}
            onConfirm={this.handleDeactivate}
            confirmLabel={i18n.t('wallet.paymentCard.detachButton')}
            cancelLabel={i18n.t('common.cancelButton')}
            confirmationText={i18n.t('wallet.paymentCard.detachConfirmationText')}
            confirmationTitle={i18n.t('wallet.paymentCard.detachConfirmationTitle')}
          >
            {i18n.t('wallet.paymentCard.detachButton')}
          </ConfirmationButton>
        </div>
      </React.Fragment>
    );
  }

  renderRefreshButton() {
    const { classes, paymentCard } = this.props;

    return (
      <div className={classes.actions}>
        <Button
          color="default"
          variant="contained"
          size="small"
          className={classes.actionButton}
          disabled={paymentCard.fetching || paymentCard.actionInProgress}
          onClick={this.handleRefresh}
        >
          <RefreshIcon style={{ fontSize: 16, marginRight: 4 }} />
          {i18n.t('wallet.paymentCard.refreshButton')}
        </Button>
      </div>
    );
  }

  renderContent() {
    const { paymentCard } = this.props;
    const { data } = paymentCard;

    if (paymentCard.fetching && !data.hasCard && !data.status) return this.renderLoading();
    if (data.pendingActivation) return this.renderPendingActivation();
    if (data.pendingDeactivation) return this.renderPendingDeactivation();
    if (data.active) return this.renderActiveCard();
    return this.renderNoCard();
  }

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <CreditCardIcon className={classes.icon} />
          <Typography variant="subtitle2" color="inherit">
            {i18n.t('wallet.paymentCard.title')}
          </Typography>
        </div>
        {this.renderContent()}
      </div>
    );
  }
}

PaymentCardPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  paymentCard: PropTypes.object.isRequired,
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(paymentCardActions, dispatch),
  };
}

function mapStateToProps(state) {
  return {
    paymentCard: state.paymentCard,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(PaymentCardPanel));
